import { Component, DestroyRef, inject, OnDestroy } from '@angular/core';
import {
  NonNullableFormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { RouterLink } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { ToastService } from '../../../core/services/toast/toast.service';
import { FormButtonComponent } from '../../../shared/components/form-controls/form-button/form-button.component';
import { PasswordInputComponent } from '../../../shared/components/form-controls/password-input/password-input.component';
import { TextInputComponent } from '../../../shared/components/form-controls/text-input/text-input.component';
import { DelegatedUIErrorI } from '../../../shared/interfaces/delegated-ui-error.interface';
import { LoginApiService } from './login-api.service';
import { LoginService } from './login.service';
import { FormValidatorService } from '../../../core/services/form-validator/form-validator.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-login',
  imports: [
    TranslateModule,
    ReactiveFormsModule,
    TextInputComponent,
    PasswordInputComponent,
    RouterLink,
    FormButtonComponent,
  ],
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss',
})
export class LoginComponent implements OnDestroy {
  private readonly destroy$ = new Subject<void>();
  isPasswordHidden = true;
  currentYear = new Date().getFullYear();
  loginForm = this.fb.group({
    credentials: [
      '',
      [Validators.required, Validators.minLength(3), Validators.maxLength(320)],
    ],
    password: [
      '',
      [
        Validators.required,
        Validators.minLength(8),
        Validators.maxLength(30),
        FormValidatorService.lowercaseLetters(),
        FormValidatorService.specialCharacters(),
        FormValidatorService.numbers(),
        FormValidatorService.uppercaseLetters(),
      ],
    ],
  });

  constructor(
    private readonly fb: NonNullableFormBuilder,
    private readonly loginApiService: LoginApiService,
    private readonly loginService: LoginService,
    private readonly toastService: ToastService
  ) {}
  private destroyRef = inject(DestroyRef);

  showHidePassword() {
    this.isPasswordHidden = !this.isPasswordHidden;
  }

  onSubmit(): void {
    this.loginApiService
      .login(this.loginForm.getRawValue())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (loginResponse) => {
          this.loginService.initLoginState(loginResponse);
        },
        error: (err: DelegatedUIErrorI) => {
          this.toastService.showError(err.description, err.title);
        },
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
